import React from 'react';
import { useSearchParams } from 'react-router-dom';
import '../styles/weeklyLearningGuideA4Print.css';
import WeeklyLearningGuideA4Document from '../components/weeklyLearningGuide/WeeklyLearningGuideA4Document';
import WeeklyLearningGuideA4CardDocument from '../components/weeklyLearningGuide/WeeklyLearningGuideA4CardDocument';
import {
  readWeeklyLearningGuideA4PreviewPayload,
  type WeeklyLearningGuideA4PreviewAction,
} from '../utils/weeklyLearningGuideA4PreviewStorage';
import {
  printWeeklyLearningGuideA4DocumentFromRoot,
  saveWeeklyLearningGuideA4DocumentAsPdfAndClose,
} from '../utils/weeklyLearningGuideA4DocumentExport';

function parseAction(raw: string | null): WeeklyLearningGuideA4PreviewAction {
  if (raw === 'pdf' || raw === 'print') return raw;
  return 'view';
}

const WeeklyLearningGuideA4PreviewPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const stashId = searchParams.get('k') ?? '';
  const action = parseAction(searchParams.get('action'));
  const stash = React.useMemo(() => readWeeklyLearningGuideA4PreviewPayload(stashId), [stashId]);
  const documentRef = React.useRef<HTMLDivElement | null>(null);
  const [autoActionDone, setAutoActionDone] = React.useState(false);
  const [renderReady, setRenderReady] = React.useState(false);
  const [autoPdfError, setAutoPdfError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      setRenderReady(true);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [stash]);

  const printTitle = stash?.exportData.titleLine1?.trim() || '주간학습 안내';
  const fileName = stash ? `${stash.fileNameBase}.pdf` : '주간학습안내.pdf';
  const isAutoPdf = action === 'pdf';

  const handlePrint = React.useCallback(() => {
    const root = documentRef.current;
    if (!root) return;
    printWeeklyLearningGuideA4DocumentFromRoot(root, printTitle);
  }, [printTitle]);

  React.useEffect(() => {
    if (!stash || !renderReady || autoActionDone) return;
    const root = documentRef.current;
    if (!root) return;

    const timer = window.setTimeout(() => {
      if (action === 'print') {
        printWeeklyLearningGuideA4DocumentFromRoot(root, printTitle);
        setAutoActionDone(true);
        return;
      }
      if (action === 'pdf') {
        void saveWeeklyLearningGuideA4DocumentAsPdfAndClose({ root, fileName })
          .catch((error) => {
            console.error('주간학습 안내 PDF 저장 실패:', error);
            setAutoPdfError('PDF 저장에 실패했습니다.');
          })
          .finally(() => {
            setAutoActionDone(true);
          });
      }
    }, 400);

    return () => window.clearTimeout(timer);
  }, [action, autoActionDone, fileName, printTitle, renderReady, stash]);

  if (!stash) {
    return (
      <div className="cp-weekly-learning-guide-a4-preview-page">
        <p className="cp-weekly-learning-guide-a4-preview-error" role="alert">
          주간학습 안내 미리보기 데이터를 찾을 수 없습니다. 원래 페이지에서 다시 시도해 주세요.
        </p>
      </div>
    );
  }

  return (
    <div
      className={`cp-weekly-learning-guide-a4-preview-page${isAutoPdf ? ' cp-weekly-learning-guide-a4-preview-page--auto-pdf' : ''}`}
    >
      {!isAutoPdf ? (
        <div className="cp-weekly-learning-guide-a4-toolbar" aria-label="주간학습 안내 A4 인쇄">
          <p className="cp-weekly-learning-guide-a4-toolbar-title">A4 미리보기</p>
          <div className="cp-weekly-learning-guide-a4-toolbar-actions">
            <button type="button" className="cp-btn-annual-action" onClick={handlePrint}>
              프린트
            </button>
          </div>
        </div>
      ) : (
        <p className="cp-weekly-learning-guide-a4-auto-pdf-status" role="status" aria-live="polite">
          {autoPdfError ?? 'PDF를 저장하는 중입니다…'}
        </p>
      )}

      <div className="cp-weekly-learning-guide-a4-preview-body">
        {isAutoPdf && !stash.exportData.neisMode ? (
          <WeeklyLearningGuideA4CardDocument ref={documentRef} payload={stash.exportData} />
        ) : (
          <WeeklyLearningGuideA4Document ref={documentRef} payload={stash.exportData} />
        )}
      </div>
    </div>
  );
};

export default WeeklyLearningGuideA4PreviewPage;
